import * as React from "react";
import {Legend, LegendItem, LegendLiElement} from "../../../components/charts/Legend";
import {ColorUtils} from "../../../components/Utils/color/ColorUtils";
import {Loader} from "../../../components/Utils/loader/Loader";
import {StatisticsUtils} from "../../../components/Utils/statistics/StatisticsUtils";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {AlarmSeverityColor, AlarmSeverityGuid} from "../../alarms/data/AlarmsMonitorData";
import {AlarmsDistributionTexts, CategoriesColors, WidgetResponse} from "../../DashboardContainer";
import {JSONMapper} from "../../data/JSONMapper";
import {StackedBarChart, StackedBarChartRow} from "../charts/StackedBarChart";
import {ZERO_GUID} from "./EquipmentByLevelWidget";
import {LegendTooltip, LegendTooltipConfig} from "./LegendTooltip";
import {AlarmByType} from "./OpenAlarmsWidget";
import {RestDataLoader} from "./RestDataLoader";
import "./style/OpenAlarmsByLevelWidget.scss";
import {LegacyBadge} from "../../../components/LegacyBadge";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {WidgetBadge, WidgetBadgeType} from "../DashboardWidget";
import AutoSizer from "react-virtualized-auto-sizer";

interface OpenAlarmsByLevelWidgetProps {
    data: WidgetResponse;
    texts: AlarmsDistributionTexts;
    colorGenerator: ColorUtils;
    maxNumberAlarmTypes?: number;
}

interface WidgetState {
    ready: boolean;
    error: boolean;
    alarmsByLevel: AlarmByCommercialNode[];
    selectedItemKey: string;
    tooltipConfig: LegendTooltipConfig;
}

export interface AlarmByCommercialNode {
    levelName: string;
    levelKey?: string;
    alarmsByType: AlarmByType[];
}

enum AlarmStatus {
    PENDING = 0,
    MANAGED = 1,
}

export const RSL_HIERARCHY_GUID = "d58245f0-5294-11e8-a5e0-525400c5f801";

declare var AppTranslations: AppTranslations;

export class OpenAlarmsByLevelWidget extends React.Component<OpenAlarmsByLevelWidgetProps, WidgetState> {
    public static getAlarmByType<T>(alarmByTypeBuckets: (T & { alarmsByType: AlarmByType[] })[]): AlarmByType[] {
        return alarmByTypeBuckets ? alarmByTypeBuckets
            .reduce<AlarmByType[]>((a, x) => [...a, ...x.alarmsByType], [])
            .reduce<AlarmByType[]>((acc, x, i) => {
                const prevIndex = acc.findIndex((y) => y.typeGuid === x.typeGuid);
                return prevIndex === -1
                    ? [...acc, x]
                    : [...acc.slice(0, prevIndex),
                        {
                            ...acc[prevIndex],
                            alarmsCount: acc[prevIndex].alarmsCount + x.alarmsCount,
                        },
                        ...acc.slice(prevIndex + 1)];
            }, [])
            .sort((a, b) => b.alarmsCount - a.alarmsCount) : [];
    }

    public static getColorBasedOnSeverity(severityGuid: string): string {
        switch (severityGuid) {
            case AlarmSeverityGuid.HIGH : {
                return AlarmSeverityColor.HIGH;
            }
            case AlarmSeverityGuid.URGENT : {
                return AlarmSeverityColor.URGENT;
            }
            case AlarmSeverityGuid.WARNING : {
                return AlarmSeverityColor.WARNING;
            }
        }

        return Legend.DEFAULT_COLOR;
    }

    public static getTextBasedOnSeverity(severityGuid: string): string {
        switch (severityGuid) {
            case AlarmSeverityGuid.HIGH : {
                return AppTranslations.equipmentAlarmFilter.highSeverityLabel;
            }
            case AlarmSeverityGuid.URGENT : {
                return AppTranslations.equipmentAlarmFilter.urgentSeverityLabel;
            }
            case AlarmSeverityGuid.WARNING : {
                return AppTranslations.equipmentAlarmFilter.warningSeverityLabel;
            }
        }
        return "Error!";
    }

    private static OTHER_ITEM_GUID = "716cb050-5176-11e9-931e-3f4cf97d5acd";

    /**
     * Given an array of legend items and the maximum number of item visible on the legend
     * orders items by value in desc mode and aggregate all exceeding items into a single
     * other item that is the sum of them.
     *
     * @param legendItems   the original legend items
     * @param maxNumber     the max number of legend items visible (Others included)
     * @param texts         the texts to compose the Others item
     */
    private static reduceLegendItems(legendItems: LegendItem[], maxNumber: number, texts: any) {
        // reduce legend items
        if (legendItems.length <= maxNumber) {
            return legendItems;
        }
        legendItems.sort((a, b) => b.value - a.value);
        const itemToAggregate: LegendItem[] = legendItems.slice(maxNumber - 1, legendItems.length);
        const newLegendItems = legendItems.slice(0, maxNumber - 1);

        const otherItem: LegendItem = {
            code: OpenAlarmsByLevelWidget.OTHER_ITEM_GUID,
            name: texts.others + " (<=" + legendItems[maxNumber].percentage.toFixed(1) + "%)",
            value: 0,
            percentage: 0,
            color: "dimgrey",
        };
        otherItem.value = itemToAggregate.reduce((a, b) => a + b.value, 0);
        otherItem.percentage = itemToAggregate.reduce((a, b) => a + b.percentage, 0);

        newLegendItems.push(otherItem);
        return newLegendItems;
    }

    private alarmByTypeByGuid: Map<string, AlarmByType>;

    constructor(props: OpenAlarmsByLevelWidgetProps) {
        super(props);
        this.state = {
            ready: false,
            error: false,
            alarmsByLevel: null,
            selectedItemKey: null,
            tooltipConfig: {visible: false},
        };
        this.setSelectedItem = this.setSelectedItem.bind(this);
        this.alarmByTypeByGuid = new Map();
    }

    public setSelectedItem(itemKey: string, e): void {
        const target: HTMLLIElement = e ? e.currentTarget : null;

        const alarm: AlarmByType = this.alarmByTypeByGuid.get(itemKey);

        this.setState({
            selectedItemKey: itemKey,
        }, () => {
            if (itemKey !== null && itemKey === OpenAlarmsByLevelWidget.OTHER_ITEM_GUID) {
                const otherAlarmTypes = OpenAlarmsByLevelWidget
                    .getAlarmByType(this.state.alarmsByLevel)
                    .filter((x, i) => i >= 11);

                if (target) {
                    this.setState({
                        tooltipConfig: {
                            visible: true,
                            offSetX: target.offsetLeft,
                            offSetY: target.offsetTop + target.offsetHeight,
                            width: target.offsetWidth,
                            data: otherAlarmTypes,
                            father: target,
                        },
                    });
                }
            }
        });
    }

    public ready() {
        if (this.state.alarmsByLevel != null) {
            this.setState({ready: true});
        }
    }

    public componentDidMount() {
        RestDataLoader.loadData(
            "statistics/alarms/counting/commercialHierarchyAndType",
            {
                statuses: "pending,managed",
                withTelemetry: true,
                placements: "warehouse,market",
            },
            (data: any) => {
                this.setState({
                    alarmsByLevel: JSONMapper.jsonAlarmsCountByCommercialHierarchyMapper(data.list),
                }, () => {
                    OpenAlarmsByLevelWidget
                        .getAlarmByType(this.state.alarmsByLevel)
                        .forEach((alarm) => {
                            this.alarmByTypeByGuid.set(alarm.typeGuid, alarm);
                        });
                });
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );
    }

    public render() {
        if (this.state.error) {
            return null;
        }
        let content = <Loader/>;
        if (!this.state.ready) {
            return (
                <div className="open-alarms-by-level-widget">
                    {content}
                </div>
            );
        }
        let data: StackedBarChartRow[] = this.state.alarmsByLevel.map(
            (item, i) => {
                return {
                    label: item.levelName,
                    subItems: item.alarmsByType.map(
                        (sitem, j) => {
                            return {
                                label: sitem.typeName,
                                code: sitem.typeGuid,
                                color: sitem.typeColor,
                                value: sitem.alarmsCount,
                            };
                        },
                    ),
                };
            },
        );

        let legendItems: LegendItem[] = [];
        // retrieve legend items from data
        let total = 0;
        data.forEach((d) => {
            d.subItems.forEach((si) => {
                const legendItem = legendItems.filter((li) => li.code === si.code);
                if (legendItems.filter((li) => li.code === si.code).length === 0) {
                    legendItems.push(
                        {
                            name: si.label,
                            code: si.code,
                            color: si.color,
                            value: si.value,
                        },
                    );
                } else {
                    legendItem[0].value += si.value;
                }
                total += si.value;
            });
        });

        // define color for each legend item
        legendItems = legendItems.map((li, i) => {
            return {
                code: li.code,
                name: li.name,
                value: li.value,
                percentage: StatisticsUtils.getPercentage(total, li.value),
                color: li.color || this.props.colorGenerator.getMappedColor(CategoriesColors.ALARM_TYPES, li.code),
                afterName: (
                    <div
                        style={{
                            display: "inline",
                            width: 65,
                        }}
                    >
                        <LegacyBadge
                            innerText={
                                OpenAlarmsByLevelWidget.getTextBasedOnSeverity(
                                    this.alarmByTypeByGuid.get(li.code).severity
                                )
                            }
                            width={42}
                            color={
                                this.alarmByTypeByGuid.get(li.code).severity === AlarmSeverityGuid.WARNING
                                    ? "#000"
                                    : "#FFF"
                            }
                            backgroundColor={OpenAlarmsByLevelWidget.getColorBasedOnSeverity(
                                this.alarmByTypeByGuid.get(li.code).severity)}
                        />
                    </div>
                ),
            };
        });

        legendItems.sort((a, b) => b.value - a.value);

        const newLegendItems = OpenAlarmsByLevelWidget.reduceLegendItems(legendItems, 12, this.props.texts);

        const legendItemToAggregated = legendItems.filter(
            (li) => !newLegendItems.map((nli) => nli.code).includes(li.code),
        );
        if (legendItems.length !== newLegendItems.length) {
            legendItems = newLegendItems;
            // reduce on original data agree with reduction performed legend items
            data.forEach((level) => {
                level.subItems = StackedBarChart.reduceSubItemsVisible(
                    level.subItems,
                    legendItemToAggregated.map((itemToAggregate) => itemToAggregate.code),
                    this.props.texts,
                    legendItems[legendItems.length - 1],
                );
            });
        }

        data = StackedBarChart.reduceBars(data, 10, this.props.texts);

        const onOtherTooltipClicked = (alarmGuid) => () => this.onItemClick(alarmGuid);
        const onOtherTooltipHover = (_, e) => null;

        content = (
            <VeBoxCard className="open-alarms-by-level-widget__container home-dashboard-card">
                <VeBoxCard.Header
                    title={this.props.texts.title}
                    titleClassName={"lowercase capitalize"}
                    className="open-alarms-by-level-widget__header technical-header space-between"
                    back={
                        <WidgetBadge type={WidgetBadgeType.COMMERCIAL_W}/>
                    }
                />
                <VeBoxCard.Body className="open-alarms-by-level-widget__content">
                    <div className="stacked-bar-chart__legend__container">
                        <Legend
                            items={legendItems}
                            selectedItemName={this.state.selectedItemKey}
                            onItemHover={this.setSelectedItem}
                            selectionKey={"code"}
                            iconOptions={{show: false}}
                            showValues={true}
                            onClick={this.onItemClick}
                            capitalize={true}
                        />
                        <LegendTooltip
                            config={this.state.tooltipConfig}
                            className={"tooltip-wrapper"}
                            style={{
                                color: "white",
                            }}
                        >
                            {(alarms: AlarmByType[]) => (
                                <div
                                    className={"tooltip-body"}
                                    style={{
                                        maxHeight: 200,
                                        overflow: "auto",
                                    }}
                                >
                                    <ul className="legend">
                                        {alarms.map((alarm, i) => (
                                            <LegendLiElement
                                                key={i}
                                                capitalize={true}
                                                item={{
                                                    name: alarm.typeName,
                                                    color: legendItems[legendItems.length - 1].color,
                                                    value: alarm.alarmsCount,
                                                    code: alarm.typeGuid,
                                                    percentage: StatisticsUtils.getPercentage(
                                                        total,
                                                        alarm.alarmsCount,
                                                    ),
                                                    afterName: (
                                                        <div
                                                            style={{
                                                                display: "inline",
                                                                width: 65,
                                                            }}
                                                        >
                                                            <LegacyBadge
                                                                innerText={OpenAlarmsByLevelWidget
                                                                    .getTextBasedOnSeverity(
                                                                        this.alarmByTypeByGuid
                                                                            .get(alarm.typeGuid).severity,
                                                                    )}
                                                                color={this.alarmByTypeByGuid
                                                                    .get(alarm.typeGuid).severity
                                                                === AlarmSeverityGuid.WARNING
                                                                    ? "#000"
                                                                    : "#FFF"}
                                                                backgroundColor={OpenAlarmsByLevelWidget
                                                                    .getColorBasedOnSeverity(
                                                                        this.alarmByTypeByGuid
                                                                            .get(alarm.typeGuid)
                                                                            .severity,
                                                                    )}
                                                                width={42}
                                                            />
                                                        </div>
                                                    ),
                                                }}
                                                iconOptions={{show: false}}
                                                selectionKey={"code"}
                                                onClick={onOtherTooltipClicked(alarm.typeGuid)}
                                                showValues={true}
                                                liStyle={{
                                                    color: "white",
                                                }}
                                                onHover={onOtherTooltipHover}
                                            />
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </LegendTooltip>
                    </div>
                    <AutoSizer style={{width: "auto"}}>
                        {({width, height}) => <StackedBarChart
                            width={width - 40}
                            height={height * .6}
                            direction={"horizontal"}
                            showValuesLabels={true}
                            addTotalsItem={false}
                            data={data}
                            legendItems={legendItems}
                            margin={{
                                top: 10,
                                bottom: 10,
                                left: 30,
                                right: 10,
                            }}
                            order={"custom"}
                            texts={{no_data_message: this.props.texts.noData}}
                            showAxesOptions={{
                                x: {line: false, tickLine: false},
                                y: {line: false, tickLine: false},
                            }}
                            legendOptions={{external: true}}
                            selectedItem={this.state.selectedItemKey}
                            onPathOver={this.setSelectedItem}
                            onClick={this.onClickSection}
                        />}
                    </AutoSizer>
                </VeBoxCard.Body>
            </VeBoxCard>
        );
        return (
            <div className="open-alarms-by-level-widget">
                {content}
            </div>
        );
    }

    private onItemClick = (d) => {
        if (d !== OpenAlarmsByLevelWidget.OTHER_ITEM_GUID) {
            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                alarmStatuses: [AlarmStatus.PENDING, AlarmStatus.MANAGED],
                withoutAlarmTypes: false,
                alarmTypeGuids: [d],
                allTelemetries: true,
                applyFilter: true,
            });
        } else {
            const otherAlarmTypesGuid = OpenAlarmsByLevelWidget.getAlarmByType(this.state.alarmsByLevel)
                .filter((x, i) => i >= 11)
                .map((x) => x.typeGuid);

            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                alarmStatuses: [AlarmStatus.PENDING, AlarmStatus.MANAGED],
                withoutAlarmTypes: false,
                alarmTypeGuids: otherAlarmTypesGuid,
                allTelemetries: true,
                applyFilter: true,
            });
        }
    }

    private onClickSection = (d) => {
        const lev = this.state.alarmsByLevel.find((x) => x.levelName === d.x);
        if (!lev) {
            return;
        }
        if (lev.levelKey !== ZERO_GUID) {
            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                commercialHierarchy: {
                    hierarchyGuid: RSL_HIERARCHY_GUID,
                    nodes: [{
                        nodeGuid: lev.levelKey,
                    }],
                },
                alarmTypeGuids: [d.y],
                alarmStatuses: [AlarmStatus.PENDING, AlarmStatus.MANAGED],
                withoutAlarmTypes: false,
                allTelemetries: true,
                applyFilter: true,
            });
        } else if (lev.levelKey === ZERO_GUID && this.state.alarmsByLevel.length === 1) {
            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                alarmStatuses: [AlarmStatus.PENDING, AlarmStatus.MANAGED],
                withoutAlarmTypes: false,
                alarmTypeGuids: [d.y],
                allTelemetries: true,
                applyFilter: true,
            });
        }
    }
}
